import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getSchema } from 'schema/Post';
import { getErrorMap } from 'components/Forms/utils/defaultErrorMap';
import RichTextEditor, {
  RichTextEditorHandle,
} from 'components/RichTextEditor/RichTextEditor';
import { Attachment } from 'schema/Post/types';
import { Space } from 'interfaces/space';
import { TFunction } from 'i18next';
import UserAvatar from 'components/UserAvatar';
import { atomic } from '@thinkific/chalk/atomic';
import { useCommunityContext } from 'components/CommunityContext';
import { Text } from '@thinkific/chalk';
import styles from './PostForm.module.scss';

interface PostFormProps {
  initialValues?: {
    title: string;
    content: string;
    spaceId: string;
    attachments: Attachment[];
    mentionedUserIds: string[];
  };
  isEdit?: boolean;
  cancelEdit?: () => void;
  onSubmit: (
    data: FormValues,
    {
      reset,
      editorRef,
    }: { reset: () => void; editorRef: React.RefObject<RichTextEditorHandle> }
  ) => Promise<void>;
  isSubmitting: boolean;
  spacesList: { value: string; label: string }[];
  selectedSpace?: Space;
  t: TFunction;
  time?: string;
}

export interface FormValues {
  title: string;
  content: string;
  spaceId: string;
  attachments: Attachment[];
  mentionedUserIds: string[];
}

const formWrapper = atomic({
  display: 'flex',
  backgroundColor: 'onLight.surface.primary',
  padding: 'space.150',
  borderRadius: 'sm',
  gap: 'space.100',
  marginBottom: 'space.200',
  boxShadow: 'onLight.shadow.sm',
});

const formContainer = atomic({
  width: 'full',
  display: 'grid',
  gap: 'space.100',
});

const postInfo = atomic({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
});

const PostForm = ({
  initialValues,
  isEdit = false,
  cancelEdit,
  onSubmit,
  isSubmitting,
  spacesList,
  selectedSpace,
  t,
  time,
}: PostFormProps) => {
  const { currentUser } = useCommunityContext();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const mentionsTextAreaRef = useRef<HTMLTextAreaElement | HTMLInputElement>(
    null
  );
  // Get user avatar info
  const { id, avatarUrl, fullName } = currentUser;
  const srcSet = `${avatarUrl}?width=40 1x, ${avatarUrl}?width=40&dpr=2 2x, ${avatarUrl}?width=40&dpr=3 3x`;

  const defaultValues = initialValues || {
    title: '',
    content: '',
    spaceId: selectedSpace?.id || spacesList[0]?.value || '',
    attachments: [],
    mentionedUserIds: [],
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues,
    resolver: zodResolver(getSchema(t), { errorMap: getErrorMap(t) }),
  });

  useEffect(() => {
    setValue('spaceId', selectedSpace?.id || spacesList[0]?.value);
  }, [spacesList, selectedSpace]);

  const handleFormSubmit = async (data) => {
    await onSubmit(data, { reset, editorRef });
  };

  return (
    <div className={formWrapper} data-qa="post-form">
      <UserAvatar
        avatarUrl={avatarUrl}
        size="sm"
        srcSet={srcSet}
        userId={id}
        username={fullName || ''}
        shouldOpenProfile
      />
      <div className={formContainer}>
        {isEdit && (
          <div className={postInfo}>
            <Text color="onLight.text.primary" variant="body.md">
              {fullName}
            </Text>
            <Text variant="body.xs">{time}</Text>
          </div>
        )}
        {!isEdit && (
          <div className={styles['post-form__space-select-container']}>
            <Text variant="body.base">
              {t(
                'components-postForm-create_new_post_in',
                'Create new post in'
              )}
            </Text>
            {selectedSpace ? (
              <Text variant="body.md">{selectedSpace.name}</Text>
            ) : (
              <Controller
                control={control}
                name="spaceId"
                render={({ field }) => (
                  <select
                    className={styles['post-form__space-select']}
                    data-qa="post-form__space-select"
                    value={field.value}
                    onChange={field.onChange}
                  >
                    {spacesList.map((space) => (
                      <option key={space.value} value={space.value}>
                        {space.label}
                      </option>
                    ))}
                  </select>
                )}
              />
            )}
          </div>
        )}
        <RichTextEditor
          cancelEdit={cancelEdit}
          // @ts-ignore
          control={control}
          errors={errors}
          isEditing={isEdit}
          isSubmitting={isSubmitting}
          mentionsTextAreaRef={mentionsTextAreaRef}
          ref={editorRef}
          setValue={setValue}
          t={t}
          showTitle
          onSubmit={handleSubmit(handleFormSubmit)}
        />
      </div>
    </div>
  );
};

export default PostForm;
